/**
 * @fileoverview Hook for background uploading images to R2.
 *
 * This hook enables non-blocking image uploads where:
 * 1. User drops/selects an image → sees it immediately (local data URL)
 * 2. Upload starts in background → `pending:<uuid>` marker stored in Yjs
 * 3. Upload completes → marker replaced with R2 URL
 *
 * Other collaborators see a loading skeleton while the upload is in progress
 * because they don't have access to the local data URL.
 *
 * ## URL Format
 * - `pending:<uuid>` - Upload in progress (only uploader can display the image)
 * - `https://...` - R2 URL (ready for all users)
 * - NEVER `data:...` - Base64 blocked by safeguard in setDraftSettings
 *
 * ## Processor Integration
 * The module-level `globalRegistry` stores upload promises that survive component
 * unmount. The submission processor can call `resolveUploadMarker()` to await the
 * same promise — no double uploads.
 */

import { useRef, useCallback, useEffect } from 'react'
import { uploadFrame } from '@/lib/api'
import { v4 as uuid } from 'uuid'

/** Prefix for pending upload markers stored in Yjs */
export const PENDING_UPLOAD_PREFIX = 'pending:'

/** Check if a value is a pending upload marker */
export function isPendingMarker(value: string | undefined | null): boolean {
  return typeof value === 'string' && value.startsWith(PENDING_UPLOAD_PREFIX)
}

/** Check if a value is a base64 data URL */
export function isBase64DataUrl(value: string | undefined | null): boolean {
  return typeof value === 'string' && value.startsWith('data:')
}

/** Extract the UUID from a pending marker */
function getPendingId(marker: string): string | null {
  if (!isPendingMarker(marker)) return null
  return marker.slice(PENDING_UPLOAD_PREFIX.length)
}

// ---------------------------------------------------------------------------
// Module-level registry: survives component unmount so the submission
// processor can resolve markers even after the panel that started the upload
// has been unmounted.
// ---------------------------------------------------------------------------

interface GlobalRegistryEntry {
  promise: Promise<string>
  dataUrl: string
  resolvedUrl?: string
}

const globalRegistry = new Map<string, GlobalRegistryEntry>()

/**
 * Resolve a pending upload marker to an R2 URL.
 *
 * Standalone export — callable from the submission processor without the hook.
 * - If the value is a `pending:<uuid>` marker, awaits the global upload promise.
 * - If the promise rejects (upload failed/cancelled), returns the stored data URL
 *   so the caller can re-upload via `preUploadFrames`.
 * - If the value is not a marker, returns it unchanged.
 */
export async function resolveUploadMarker(marker: string): Promise<string> {
  const id = getPendingId(marker)
  if (!id) return marker

  const entry = globalRegistry.get(id)
  if (!entry) return marker // Unknown or already cleaned up

  // If the promise already resolved and we cached the URL, return it directly
  if (entry.resolvedUrl) return entry.resolvedUrl

  try {
    return await entry.promise
  } catch {
    // Upload failed — return the raw data URL so preUploadFrames can retry
    return entry.dataUrl
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface PendingUpload {
  localId: string
  dataUrl: string
}

interface UseBackgroundImageUploadReturn {
  /**
   * Start uploading an image in the background.
   *
   * @param dataUrl - The base64 data URL of the image
   * @param onSuccess - Called when upload completes with the R2 URL
   * @param onError - Called when upload fails
   * @returns The pending marker (e.g., `pending:abc123`) to store in state/Yjs
   */
  startUpload: (dataUrl: string, onSuccess: (r2Url: string) => void, onError: (error: Error) => void) => string

  /**
   * Start uploading after asynchronous preprocessing while still returning a
   * pending marker immediately. The original data URL remains available for
   * local preview while the upload promise waits for the processed data URL.
   */
  startUploadDeferred: (
    displayDataUrl: string,
    getUploadDataUrl: () => Promise<string>,
    onSuccess: (r2Url: string) => void,
    onError: (error: Error) => void,
    reuseKey?: string,
  ) => string

  /**
   * Get the local data URL for a pending marker.
   * Returns null if this isn't a pending marker we started (e.g. collaborator upload).
   */
  getLocalDataUrl: (markerOrUrl: string) => string | null

  /** Check if a value is a pending upload marker. */
  isPending: (value: string | undefined | null) => boolean

  /** Cancel display tracking. Does NOT abort in-flight uploads. */
  cancelAll: () => void
}

export function useBackgroundImageUpload(): UseBackgroundImageUploadReturn {
  // Component-level ref for display (getLocalDataUrl). Cleaned up on unmount.
  const pendingUploadsRef = useRef<Map<string, PendingUpload>>(new Map())
  const reusableMarkersRef = useRef<Map<string, string>>(new Map())

  // Cleanup local display tracking on unmount.
  // We intentionally do NOT abort uploads — the global registry keeps the
  // promises alive for the submission processor.
  useEffect(() => {
    return () => {
      pendingUploadsRef.current.clear()
    }
  }, [])

  const startUploadDeferred = useCallback(
    (
      displayDataUrl: string,
      getUploadDataUrl: () => Promise<string>,
      onSuccess: (r2Url: string) => void,
      onError: (error: Error) => void,
      reuseKey?: string,
    ): string => {
      if (reuseKey) {
        const existingMarker = reusableMarkersRef.current.get(reuseKey)
        const existingId = existingMarker ? getPendingId(existingMarker) : null
        if (existingMarker && existingId && pendingUploadsRef.current.has(existingId)) {
          return existingMarker
        }
      }

      const localId = uuid()
      const marker = `${PENDING_UPLOAD_PREFIX}${localId}`

      // Create the upload promise
      const promise = (async () => {
        const uploadDataUrl = await getUploadDataUrl()
        const entry = globalRegistry.get(localId)
        if (entry) entry.dataUrl = uploadDataUrl
        const result = await uploadFrame(uploadDataUrl)
        return result.url
      })()

      // Register in global registry (for processor resolution)
      globalRegistry.set(localId, { promise, dataUrl: displayDataUrl })

      // Register in local ref (for display via getLocalDataUrl)
      pendingUploadsRef.current.set(localId, { localId, dataUrl: displayDataUrl })
      if (reuseKey) {
        reusableMarkersRef.current.set(reuseKey, marker)
      }

      // Wire up callbacks + cleanup.
      // On success, cache the resolved URL and defer registry cleanup so the
      // submission processor can still look it up if React hasn't re-rendered yet.
      promise.then(
        r2Url => {
          const entry = globalRegistry.get(localId)
          if (entry) entry.resolvedUrl = r2Url
          pendingUploadsRef.current.delete(localId)
          if (reuseKey && reusableMarkersRef.current.get(reuseKey) === marker) {
            reusableMarkersRef.current.delete(reuseKey)
          }
          onSuccess(r2Url)
          // Defer global registry cleanup to avoid race with processor
          setTimeout(() => globalRegistry.delete(localId), 30_000)
        },
        error => {
          globalRegistry.delete(localId)
          pendingUploadsRef.current.delete(localId)
          if (reuseKey && reusableMarkersRef.current.get(reuseKey) === marker) {
            reusableMarkersRef.current.delete(reuseKey)
          }
          onError(error instanceof Error ? error : new Error('Upload failed'))
        },
      )

      return marker
    },
    [],
  )

  const startUpload = useCallback(
    (dataUrl: string, onSuccess: (r2Url: string) => void, onError: (error: Error) => void): string =>
      startUploadDeferred(dataUrl, async () => dataUrl, onSuccess, onError),
    [startUploadDeferred],
  )

  const getLocalDataUrl = useCallback((markerOrUrl: string): string | null => {
    const pendingId = getPendingId(markerOrUrl)
    if (!pendingId) return null
    return pendingUploadsRef.current.get(pendingId)?.dataUrl ?? null
  }, [])

  const isPending = useCallback((value: string | undefined | null): boolean => {
    return isPendingMarker(value)
  }, [])

  const cancelAll = useCallback((): void => {
    pendingUploadsRef.current.clear()
  }, [])

  return {
    startUpload,
    startUploadDeferred,
    getLocalDataUrl,
    isPending,
    cancelAll,
  }
}
